// src/components/Header.jsx
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../AuthContext";
import { useDarkMode } from "../DarkModeContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { loadProfileHybrid } from "../userDataSync";

// Avatar color options - Brand colors
const avatarColors = {
  "blue-green": "from-[#8FB3D3] to-[#385B37]",
  "sunset-orange": "from-[#F8662B] to-[#F8662B]",
  "earth-tone": "from-[#E4B4A6] to-[#413C04]",
  "forest-green": "from-[#385B37] to-[#385B37]",
  "sky-blue": "from-[#8FB3D3] to-[#8FB3D3]",
  "warm-peach": "from-[#E4B4A6] to-[#F8662B]",
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [avatarColor, setAvatarColor] = useState("from-blue-500 to-purple-500");
  const { user } = useAuth();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Load profile preferences (hybrid: Firebase first, fallback to localStorage)
  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        try {
          const profile = await loadProfileHybrid(user.uid, user.email);

          if (profile) {
            setDisplayName(profile.displayName || "");
            setAvatarColor(avatarColors[profile.avatarColor] || "from-[#8FB3D3] to-[#385B37]");
          } else {
            // Reset to defaults if no profile saved
            setDisplayName("");
            setAvatarColor("from-[#8FB3D3] to-[#385B37]");
          }
        } catch (err) {
          console.error("Error loading profile:", err);
          // Reset to defaults on error
          setDisplayName("");
          setAvatarColor("from-[#8FB3D3] to-[#385B37]");
        }
      }
    };

    loadProfile();

    // Listen for profile updates
    window.addEventListener('profileUpdated', loadProfile);
    return () => window.removeEventListener('profileUpdated', loadProfile);
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setShowMenu(false);
      navigate("/");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? darkMode
            ? "backdrop-blur-xl shadow-sm"
            : "backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      }`}
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        ...(scrolled ? {
          backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 250, 223, 0.95)',
          borderBottom: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
        } : {})
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo - left aligned */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/images/flatlogo.png"
            alt="Tour in a Box"
            className="h-8 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* Right side actions */}
        <div className="flex items-center gap-3">
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all"
                style={{
                  backgroundColor: darkMode ? 'rgb(31, 41, 55)' : 'rgba(228, 180, 166, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? 'rgb(55, 65, 81)' : 'rgba(228, 180, 166, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? 'rgb(31, 41, 55)' : 'rgba(228, 180, 166, 0.3)';
                }}
              >
                <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white text-xs font-bold`}>
                  {(displayName || user.email)?.[0]?.toUpperCase()}
                </div>
                <span className={`text-sm font-medium hidden sm:inline ${
                  darkMode ? "text-gray-200" : "text-gray-700"
                }`}>
                  {displayName || user.email?.split('@')[0]}
                </span>
              </button>

              {/* Dropdown Menu */}
              {showMenu && (
                <div
                  className="absolute right-0 mt-2 w-48 rounded-xl shadow-xl overflow-hidden"
                  style={{
                    backgroundColor: darkMode ? 'rgb(31, 41, 55)' : 'rgba(255, 250, 223, 0.98)',
                    border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
                  }}
                >
                  <div
                    className="px-4 py-3"
                    style={{
                      borderBottom: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid rgba(228, 180, 166, 0.3)'
                    }}
                  >
                    <div className={`text-sm font-semibold ${
                      darkMode ? "text-gray-100" : "text-gray-900"
                    }`}>
                      {displayName || user.email?.split('@')[0]}
                    </div>
                    <div className={`text-xs truncate ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}>
                      {user.email}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/profile');
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                      darkMode ? "text-gray-200 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Profile
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2.5 text-left text-sm transition-colors"
                    style={{ color: '#dc2626' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? 'rgba(220, 38, 38, 0.2)' : 'rgba(220, 38, 38, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="px-4 py-2 rounded-full text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#F8662B' }}
            >
              Log In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
